import { invariant } from '@spotify/confidence-core-react';
import { StatsDataFragment } from '@spotify/confidence-graphql';
import {
  useIsStatsModuleStrategySequentialV2,
  useWorkflowInstanceContext,
} from '@spotify/plugin-confidence-workflows-react';

import { convertAnalysisResults } from '../helpers';
import { AnalysisResult, StatsData2 } from '../types';

type UseStatsDataOptions = {
  exposureFilter?: string;
};

const emptyAnalysisResult: AnalysisResult = {
  results: [],
  annotations: {},
  recommendations: [],
};

export const useStatsData = ({
  exposureFilter = 'base',
}: UseStatsDataOptions = {}): StatsData2 => {
  const { hasLaunched, isLive, moduleData2 } = useWorkflowInstanceContext();

  const statsData: StatsDataFragment | undefined | null = moduleData2?.stats;
  invariant(statsData, 'useStatsData: stats data is required');

  const isSequential = useIsStatsModuleStrategySequentialV2(statsData);
  const isFixedHorizon = !isSequential;
  const canHaveResults =
    hasLaunched && (isSequential || (isFixedHorizon && !isLive));
  const analysisResults = convertAnalysisResults(statsData.analysisResults);
  const selectedAnalysisResult =
    analysisResults.length === 1
      ? analysisResults[0]
      : (analysisResults.find(
          result => result.exposureFilter === exposureFilter,
        ) ?? emptyAnalysisResult);
  const selectedAnalysisHasResults = selectedAnalysisResult.results.some(
    result => (result.result?.length ?? 0) > 0,
  );

  return {
    analysisResults,
    selectedAnalysisResult,
    isSequential,
    isFixedHorizon,
    canHaveResults,
    hasAvailableResults: canHaveResults && selectedAnalysisHasResults,
  };
};
